import { Component, OnDestroy, OnInit, inject, output, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SubSink } from 'subsink';
import { RegisterModel } from '../../core/models/auth/register.model';
import { FormControlNameRecord } from '../../shared/data-types/form-types';
import { MustMatch } from '../../shared/validators/must-match.validator';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  standalone: false,
})
export class RegisterComponent implements OnInit, OnDestroy {
  registerForm = signal<FormGroup | null>(null);
  readonly formSubmitted$ = output<RegisterModel>();
  readonly showLogin$ = output<void>();

  // Forms
  public formControlNames: FormControlNameRecord = {
    FirstName: 'firstName',
    LastName: 'lastName',
    Email: 'email',
    Password: 'password',
    PasswordRepeat: 'passwordRepeat',
  };

  // DI
  private readonly formBuilder = inject(FormBuilder);

  // Misc
  private mustMatchEnabled = false;
  private emailValidatorEnabled = false;
  private passwordMinLengthEnabled = false;
  private readonly subs = new SubSink();

  ngOnInit(): void {
    this.registerForm.set(
      this.formBuilder.group({
        [this.formControlNames.FirstName]: ['', Validators.required],
        [this.formControlNames.LastName]: ['', Validators.required],
        [this.formControlNames.Email]: ['', Validators.required],
        [this.formControlNames.Password]: ['', Validators.required],
        [this.formControlNames.PasswordRepeat]: ['', Validators.required],
      }),
    );
  }

  ngOnDestroy(): void {
    this.showLogin();
    this.subs.unsubscribe();
  }

  public register(): void {
    // Enable email format validation only after first submit
    if (!this.emailValidatorEnabled) {
      const emailCtrl = this.registerForm().get(this.formControlNames.Email);
      emailCtrl.setValidators([Validators.required, Validators.email]);
      emailCtrl.updateValueAndValidity({ emitEvent: false });
      this.emailValidatorEnabled = true;
    }

    // Enable mustMatch only after first submit
    if (!this.mustMatchEnabled) {
      this.registerForm().setValidators(
        MustMatch(this.formControlNames.Password, this.formControlNames.PasswordRepeat),
      );
      this.registerForm().updateValueAndValidity({ emitEvent: false });
      this.mustMatchEnabled = true;
    }

    // Enable minLength only after first submit
    if (!this.passwordMinLengthEnabled) {
      const passwordCtrl = this.registerForm().get(this.formControlNames.Password);
      passwordCtrl.setValidators([Validators.required, Validators.minLength(8)]);
      passwordCtrl.updateValueAndValidity({ emitEvent: false });
      this.passwordMinLengthEnabled = true;
    }

    this.registerForm().markAllAsTouched();

    if (!this.registerForm().valid) {
      return;
    }

    const registerModel = new RegisterModel();
    registerModel.firstName = this.registerForm().controls[this.formControlNames.FirstName].value;
    registerModel.lastName = this.registerForm().controls[this.formControlNames.LastName].value;
    registerModel.email = this.registerForm().controls[this.formControlNames.Email].value;
    registerModel.password = this.registerForm().controls[this.formControlNames.Password].value;
    registerModel.passwordRepeat =
      this.registerForm().controls[this.formControlNames.PasswordRepeat].value;
    this.formSubmitted$.emit(registerModel);
  }

  public showLogin(): void {
    this.showLogin$.emit();
  }
}
